import React, { useEffect, useMemo, useState } from 'react';
import { FiRefreshCw, FiAlertCircle, FiTable } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { loadBhphAccounts } from '../../services/bhphAccounts';
import AmortizationModal from './AmortizationModal';

const money = (v) =>
  Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dateStr = (d) =>
  d instanceof Date ? `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}` : '—';

const Stat = ({ label, value, alert }) => (
  <div className="flex justify-between gap-4 text-sm py-0.5">
    <span className="text-gray-500">{label}</span>
    <span className={alert ? 'font-semibold text-red-600' : 'text-gray-800'}>{value}</span>
  </div>
);

// DeskManager-style BHPH receivables list: one card per seller-financed
// account with late-days badge, due amount, and balances.
const BHPHAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('late');
  const [amortModel, setAmortModel] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      setAccounts(await loadBhphAccounts());
    } catch (err) {
      console.error('Failed to load BHPH accounts:', err);
      setError(err.message || 'Failed to load BHPH accounts.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const sorted = useMemo(() => {
    const list = [...accounts];
    if (sortBy === 'late') list.sort((a, b) => b.daysLate - a.daysLate || b.dueAmount - a.dueAmount);
    if (sortBy === 'balance') list.sort((a, b) => b.principalBalance - a.principalBalance);
    if (sortBy === 'recent') list.sort((a, b) => new Date(b.deal.created_at) - new Date(a.deal.created_at));
    return list;
  }, [accounts, sortBy]);

  const totals = useMemo(() => ({
    balance: accounts.reduce((s, a) => s + a.principalBalance, 0),
    due: accounts.reduce((s, a) => s + a.dueAmount, 0),
    late: accounts.filter((a) => a.daysLate > 0).length,
  }), [accounts]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">BHPH Accounts</h1>
          <p className="text-sm text-gray-600">
            Seller-financed accounts from saved Deal Builder deals.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="late">Most late first</option>
            <option value="balance">Highest balance first</option>
            <option value="recent">Newest deals first</option>
          </select>
          <button
            type="button"
            onClick={refresh}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1.5"
          >
            <SafeIcon icon={FiRefreshCw} className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Portfolio totals */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500">Open principal</p>
          <p className="text-2xl font-bold text-gray-900">${money(totals.balance)}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500">Past due (site payments only)</p>
          <p className="text-2xl font-bold text-red-600">${money(totals.due)}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-500">Accounts behind</p>
          <p className="text-2xl font-bold text-gray-900">{totals.late} of {accounts.length}</p>
        </div>
      </div>

      <div className="text-xs text-gray-500 flex items-start gap-1.5">
        <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Received amounts count website payments matched by customer email. Cash or
          money-order payments taken in the office are not included, so an account can
          show behind here while current in your records.
        </span>
      </div>

      {error && (
        <div className="bg-red-50 text-red-800 text-sm rounded-lg px-4 py-2">{error}</div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 py-8 text-center">Loading accounts…</p>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-sm text-gray-500">
          No BHPH deals found. Deals appear here after they're saved from the Deal Builder
          with deal type “Buy Here Pay Here”.
        </div>
      ) : (
        <div className="space-y-3">
          {sorted.map((a) => (
            <div key={a.deal.id} className="bg-white rounded-xl shadow p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-900">{a.deal.customer_name || 'Unknown buyer'}</p>
                  <p className="text-sm text-gray-600">
                    {a.vehicle
                      ? `${a.vehicle.year} ${a.vehicle.make} ${a.vehicle.model}`
                      : 'No vehicle linked'}
                    {a.vehicle?.stock_number ? ` · Stock #${a.vehicle.stock_number}` : ''}
                  </p>
                  <p className="text-xs text-gray-400">
                    Sold {new Date(a.deal.created_at).toLocaleDateString()} ·
                    ${money(a.paymentAmount)} {a.frequency.toLowerCase()} × {a.term} @ {a.apr}%
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {!a.hasSchedule ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
                      No schedule — set first payment date
                    </span>
                  ) : a.daysLate > 0 ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      Late days: {a.daysLate}
                    </span>
                  ) : (
                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                      Current
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => setAmortModel(a.deal.forms_data)}
                    className="px-2.5 py-1 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-1"
                    title="Amortization schedule"
                  >
                    <SafeIcon icon={FiTable} className="w-3.5 h-3.5" /> Schedule
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 mt-3 pt-3 border-t">
                <div>
                  <Stat label="Due amount" value={`$${money(a.dueAmount)}`} alert={a.dueAmount > 0} />
                  <Stat label="Next due date" value={dateStr(a.nextDueDate)} />
                  <Stat label="Last site payment" value={dateStr(a.lastPaymentDate)} />
                </div>
                <div>
                  <Stat label="Principal balance" value={`$${money(a.principalBalance)}`} />
                  <Stat label="Amount financed" value={`$${money(a.amountFinanced)}`} />
                  <Stat label="Received (site)" value={`$${money(a.received)} (${a.paymentsCount})`} />
                </div>
                <div>
                  <Stat label="Payments made" value={a.hasSchedule ? `${a.paidPeriods} of ${a.term}` : '—'} />
                  <Stat label="Payments due to date" value={a.hasSchedule ? a.periodsDue : '—'} />
                  <Stat label="Buyer contact" value={a.deal.customer_phone || a.deal.customer_email || '—'} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {amortModel && (
        <AmortizationModal model={amortModel} onClose={() => setAmortModel(null)} />
      )}
    </div>
  );
};

export default BHPHAccounts;
